import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Select, Row, Col, Spin, Space, Empty, Progress } from "antd";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
    CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
    ClockCircleOutlined, RiseOutlined, ExclamationCircleOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

export default function AttendanceReport() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(CURRENT_YEAR);
    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState("ALL");

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = { month, year };
        if (organisationId !== "ALL") params.organisation_id = organisationId;

        axios.get(`${backUrl}/api/reports/attendance-summary/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setData(res.data || null))
          .catch(() => setData(null))
          .finally(() => setLoading(false));
    }, [organisationId, month, year, backUrl, authData?.access]);

    const presentPct = Number(data?.present_percentage || 0);
    const absentPct  = Number(data?.absent_percentage  || 0);
    const lateDays   = Number(data?.late_days          || 0);
    const lopDays    = Number(data?.lop_days           || 0);
    const otHours    = Number(data?.overtime_hours     || 0);

    const kpis = [
        {
            label: "Present Rate",
            val: `${presentPct}%`,
            icon: <CheckCircleOutlined />,
            color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0",
            progress: presentPct, showProgress: true,
        },
        {
            label: "Absent Rate",
            val: `${absentPct}%`,
            icon: <CloseCircleOutlined />,
            color: "#ef4444", bg: "#fff1f2", border: "#fecaca",
            progress: absentPct, showProgress: true,
        },
        {
            label: "Late Days",
            val: lateDays,
            icon: <ClockCircleOutlined />,
            color: "#f59e0b", bg: "#fffbeb", border: "#fde68a",
            showProgress: false,
        },
        {
            label: "LOP Days",
            val: lopDays,
            icon: <ExclamationCircleOutlined />,
            color: "#dc2626", bg: "#fff1f2", border: "#fecaca",
            showProgress: false,
        },
        {
            label: "OT Hours",
            val: otHours,
            icon: <RiseOutlined />,
            color: "#0e497b", bg: "#eff6ff", border: "#bfdbfe",
            showProgress: false,
        },
    ];

    const chartData = data ? [
        { name: "Present %", value: presentPct, fill: "#10b981" },
        { name: "Absent %",  value: absentPct,  fill: "#ef4444" },
    ] : [];

    return (
        <div style={{
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: 16, border: "1px solid #e2e8f0",
            boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
            overflow: "hidden", height: "100%",
        }}>
            <div style={{ height: 3.5, background: "linear-gradient(90deg, #0e497b, #3b82f6)" }} />
            <div style={{ padding: "16px 20px" }}>
                {/* Header */}
                <Row justify="space-between" align="middle" gutter={[10, 10]} style={{ marginBottom: 16 }}>
                    <Col>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <CalendarOutlined style={{ color: "#0e497b", fontSize: 18 }} />
                            <div>
                                <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                                    Monthly Attendance Summary
                                </div>
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                                    {MONTHS[month - 1]} {year} — Attendance KPIs
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col>
                        <Space size={6} wrap>
                            <Select value={organisationId} onChange={setOrganisationId} style={{ width: 160 }} size="small">
                                <Option value="ALL">All Organisations ({organisations.length})</Option>
                                {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                            </Select>
                            <Select value={month} onChange={setMonth} style={{ width: 108 }} size="small">
                                {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
                            </Select>
                            <Select value={year} onChange={setYear} style={{ width: 80 }} size="small">
                                {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
                            </Select>
                        </Space>
                    </Col>
                </Row>

                {loading ? (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>
                            Loading Attendance Summary...
                        </div>
                    </div>
                ) : !data ? (
                    <Empty description="No attendance summary data available" style={{ padding: "50px 0" }} />
                ) : (
                    <>
                        {/* KPI Cards */}
                        <Row gutter={[10, 10]} style={{ marginBottom: 18 }}>
                            {kpis.map(k => (
                                <Col xs={12} sm={k.showProgress ? 12 : 8} key={k.label}>
                                    <div style={{
                                        background: k.bg, border: `1px solid ${k.border}`,
                                        borderRadius: 12, padding: "11px 14px",
                                    }}>
                                        <div style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            marginBottom: 6,
                                        }}>
                                            <span style={{ color: k.color, fontSize: 14 }}>{k.icon}</span>
                                            <span style={{
                                                fontSize: 10, fontWeight: 700, color: k.color,
                                                textTransform: "uppercase", letterSpacing: "0.05em",
                                            }}>
                                                {k.label}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 22, fontWeight: 900, color: k.color, lineHeight: 1, marginBottom: k.showProgress ? 6 : 0 }}>
                                            {k.val}
                                        </div>
                                        {k.showProgress && (
                                            <Progress
                                                percent={Math.min(k.progress, 100)}
                                                strokeColor={k.color}
                                                trailColor="#e2e8f0"
                                                showInfo={false}
                                                strokeWidth={6}
                                                style={{ margin: 0 }}
                                            />
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {/* Bar Chart — Present vs Absent */}
                        {chartData.length > 0 && (
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }} barSize={40}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }} />
                                    <YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={v => `${v}%`} />
                                    <Tooltip formatter={v => [`${v}%`]} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                                    <Bar dataKey="value" name="Rate" radius={[6, 6, 0, 0]}>
                                        {chartData.map((entry, i) => (
                                            <rect key={i} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
